import React, { useState, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green, red, orange, blue } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ConnectIcon,
  Stop as DisconnectIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Phone as PhoneIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastUtils from "../../utils/toastUtils";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
  },
  textField: {
    marginBottom: theme.spacing(1),
  },
  formControl: {
    marginBottom: theme.spacing(1),
    minWidth: 120,
  },
  statusChip: {
    fontWeight: "bold",
  },
  connected: {
    backgroundColor: green[100],
    color: green[800],
  },
  disconnected: {
    backgroundColor: red[100],
    color: red[800],
  },
  error: {
    backgroundColor: red[100],
    color: red[800],
  },
  connecting: {
    backgroundColor: orange[100],
    color: orange[800],
  },
  cardStats: {
    marginBottom: theme.spacing(2),
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  statCard: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  statNumber: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
}));

const AsteriskConnections = () => {
  const classes = useStyles();

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    connected: 0,
    disconnected: 0,
    error: 0
  });

  const [connectionForm, setConnectionForm] = useState({
    name: "",
    host: "",
    port: 8088,
    username: "",
    password: "",
    secure: false,
    isActive: true
  });

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/asterisk-connections", {
        params: { searchParam, pageNumber }
      });

      if (pageNumber === 1) {
        setConnections(data.asteriskConnections);
      } else {
        setConnections(prev => [...prev, ...data.asteriskConnections]);
      }

      setHasMore(data.hasMore);

      // Calcular estatísticas
      const statsData = data.asteriskConnections.reduce((acc, conn) => {
        acc.total++;
        acc[conn.status] = (acc[conn.status] || 0) + 1;
        return acc;
      }, { total: 0, connected: 0, disconnected: 0, error: 0, connecting: 0 });

      setStats(statsData);
    } catch (err) {
      toastUtils.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleOpenConnectionModal = (connection = null) => {
    if (connection) {
      setConnectionForm({
        name: connection.name,
        host: connection.host,
        port: connection.port,
        username: connection.username,
        password: connection.password,
        secure: connection.secure,
        isActive: connection.isActive
      });
      setSelectedConnection(connection);
    } else {
      setConnectionForm({
        name: "",
        host: "",
        port: 8088,
        username: "",
        password: "",
        secure: false,
        isActive: true
      });
      setSelectedConnection(null);
    }
    setConnectionModalOpen(true);
  };

  const handleCloseConnectionModal = () => {
    setConnectionModalOpen(false);
    setSelectedConnection(null);
    setConnectionForm({
      name: "",
      host: "",
      port: 8088,
      username: "",
      password: "",
      secure: false,
      isActive: true
    });
  };

  const handleSaveConnection = async () => {
    try {
      if (selectedConnection) {
        // Atualizar conexão existente
        await api.put(`/asterisk-connections/${selectedConnection.id}`, connectionForm);
        toastUtils.success(i18n.t("asterisk.toasts.connectionUpdated"));
      } else {
        // Criar nova conexão
        await api.post("/asterisk-connections", connectionForm);
        toastUtils.success(i18n.t("asterisk.toasts.connectionCreated"));
      }

      handleCloseConnectionModal();
      setPageNumber(1);
      fetchConnections();
    } catch (err) {
      toastUtils.error(err);
    }
  };

  const handleDeleteConnection = async () => {
    try {
      await api.delete(`/asterisk-connections/${selectedConnection.id}`);
      toastUtils.success(i18n.t("asterisk.toasts.connectionDeleted"));
      setConfirmModalOpen(false);
      setSelectedConnection(null);
      setPageNumber(1);
      fetchConnections();
    } catch (err) {
      toastUtils.error(err);
    }
  };

  const handleTestConnection = async (connection) => {
    try {
      const { data } = await api.post(`/asterisk-connections/${connection.id}/test`);
      if (data.success) {
        toastUtils.success(i18n.t("asterisk.toasts.connectionTested"));
      } else {
        toastUtils.error(data.message);
      }
      fetchConnections();
    } catch (err) {
      toastUtils.error(err);
    }
  };

  const handleToggleConnection = async (connection) => {
    try {
      if (connection.status === "connected") {
        await api.post(`/asterisk-connections/${connection.id}/disconnect`);
        toastUtils.success(i18n.t("asterisk.toasts.connectionDisconnected"));
      } else {
        await api.post(`/asterisk-connections/${connection.id}/connect`);
        toastUtils.success(i18n.t("asterisk.toasts.connectionConnected"));
      }
      fetchConnections();
    } catch (err) {
      toastUtils.error(err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <CheckCircleIcon style={{ color: green[500] }} />;
      case "connecting":
        return <SyncIcon style={{ color: orange[500] }} />;
      case "error":
        return <ErrorIcon style={{ color: red[500] }} />;
      default:
        return <ErrorIcon style={{ color: red[300] }} />;
    }
  };

  const getStatusChip = (status) => {
    const className = `${classes.statusChip} ${classes[status] || classes.disconnected}`;
    return (
      <Chip
        size="small"
        label={i18n.t(`asterisk.${status}`)}
        className={className}
      />
    );
  };

  const loadMore = () => {
    setPageNumber(prev => prev + 1);
  };

  const handleSearch = (searchValue) => {
    setSearchParam(searchValue);
    setPageNumber(1);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("asterisk.connections.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenConnectionModal()}
            startIcon={<AddIcon />}
          >
            {i18n.t("asterisk.newConnection")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Cards de Estatísticas */}
      <Box className={classes.statsGrid}>
        <Card className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statNumber}>
              {stats.total}
            </Typography>
            <Typography className={classes.statLabel}>
              Total de Conexões
            </Typography>
          </CardContent>
        </Card>

        <Card className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statNumber} style={{ color: green[500] }}>
              {stats.connected || 0}
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t("asterisk.connected")}
            </Typography>
          </CardContent>
        </Card>

        <Card className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statNumber} style={{ color: red[500] }}>
              {stats.disconnected || 0}
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t("asterisk.disconnected")}
            </Typography>
          </CardContent>
        </Card>

        <Card className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statNumber} style={{ color: red[600] }}>
              {stats.error || 0}
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t("asterisk.error")}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("asterisk.status")}</TableCell>
              <TableCell>{i18n.t("asterisk.connectionName")}</TableCell>
              <TableCell>{i18n.t("asterisk.host")}</TableCell>
              <TableCell align="center">{i18n.t("asterisk.port")}</TableCell>
              <TableCell>{i18n.t("asterisk.username")}</TableCell>
              <TableCell align="center">{i18n.t("asterisk.secure")}</TableCell>
              <TableCell align="center">{i18n.t("asterisk.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {connections.map((connection) => (
                <TableRow key={connection.id}>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      {getStatusIcon(connection.status)}
                      {getStatusChip(connection.status)}
                    </Box>
                  </TableCell>
                  <TableCell>{connection.name}</TableCell>
                  <TableCell>{connection.host}</TableCell>
                  <TableCell align="center">{connection.port}</TableCell>
                  <TableCell>{connection.username}</TableCell>
                  <TableCell align="center">
                    {connection.secure ?
                      <CheckCircleIcon style={{ color: green[500] }} /> :
                      <ErrorIcon style={{ color: red[300] }} />
                    }
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={i18n.t("asterisk.testConnection")}>
                      <IconButton
                        size="small"
                        onClick={() => handleTestConnection(connection)}
                      >
                        <SyncIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={
                      connection.status === "connected" ?
                        i18n.t("asterisk.disconnect") :
                        i18n.t("asterisk.connect")
                    }>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleConnection(connection)}
                        style={{
                          color: connection.status === "connected" ? red[500] : green[500]
                        }}
                      >
                        {connection.status === "connected" ?
                          <DisconnectIcon /> :
                          <ConnectIcon />
                        }
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={i18n.t("asterisk.editConnection")}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenConnectionModal(connection)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={i18n.t("asterisk.deleteConnection")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedConnection(connection);
                          setConfirmModalOpen(true);
                        }}
                        disabled={connection.status === "connected"}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={7} />}
            </>
          </TableBody>
        </Table>
      </Paper>

      {/* Modal de Conexão */}
      <Dialog
        open={connectionModalOpen}
        onClose={handleCloseConnectionModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedConnection ?
            i18n.t("asterisk.editConnection") :
            i18n.t("asterisk.newConnection")
          }
        </DialogTitle>
        <DialogContent>
          <TextField
            label={i18n.t("asterisk.connectionName")}
            fullWidth
            value={connectionForm.name}
            onChange={(e) => setConnectionForm({...connectionForm, name: e.target.value})}
            className={classes.textField}
            required
          />

          <TextField
            label={i18n.t("asterisk.host")}
            fullWidth
            value={connectionForm.host}
            onChange={(e) => setConnectionForm({...connectionForm, host: e.target.value})}
            className={classes.textField}
            required
          />

          <TextField
            label={i18n.t("asterisk.port")}
            type="number"
            fullWidth
            value={connectionForm.port}
            onChange={(e) => setConnectionForm({...connectionForm, port: parseInt(e.target.value)})}
            className={classes.textField}
            required
          />

          <TextField
            label={i18n.t("asterisk.username")}
            fullWidth
            value={connectionForm.username}
            onChange={(e) => setConnectionForm({...connectionForm, username: e.target.value})}
            className={classes.textField}
            required
          />

          <TextField
            label={i18n.t("asterisk.password")}
            type="password"
            fullWidth
            value={connectionForm.password}
            onChange={(e) => setConnectionForm({...connectionForm, password: e.target.value})}
            className={classes.textField}
            required
          />

          <FormControlLabel
            control={
              <Switch
                checked={connectionForm.secure}
                onChange={(e) => setConnectionForm({...connectionForm, secure: e.target.checked})}
                color="primary"
              />
            }
            label={i18n.t("asterisk.secure")}
          />

          <FormControlLabel
            control={
              <Switch
                checked={connectionForm.isActive}
                onChange={(e) => setConnectionForm({...connectionForm, isActive: e.target.checked})}
                color="primary"
              />
            }
            label="Ativo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConnectionModal}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveConnection}
            color="primary"
            variant="contained"
            disabled={!connectionForm.name || !connectionForm.host || !connectionForm.username || !connectionForm.password}
          >
            {selectedConnection ? "Atualizar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação */}
      <ConfirmationModal
        title={i18n.t("asterisk.deleteConnection")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteConnection}
      >
        Tem certeza que deseja deletar a conexão "{selectedConnection?.name}"?
      </ConfirmationModal>
    </MainContainer>
  );
};

export default AsteriskConnections;